/**
 * Prompt templates for AI-powered template generation
 */

export interface PromptContext {
  userInput: string;
  industry?: string;
  templateType?: string;
  userHistory?: string[];
  existingTemplates?: string[];
  difficulty?: 'beginner' | 'intermediate' | 'advanced';
  estimatedDuration?: string;
}

export interface GeneratedTemplate {
  name: string;
  description: string;
  category: string;
  elements: Array<{
    type: 'question' | 'statement' | 'instruction' | 'note';
    content: string;
    category: 'discovery' | 'presentation' | 'objection_handling' | 'closing' | 'follow_up';
    required: boolean;
    placeholder?: string;
  }>;
  metadata: {
    estimatedDuration: string;
    difficulty: 'beginner' | 'intermediate' | 'advanced';
    industry: string;
    tags: string[];
  };
}

// Base system prompt for template generation
const SYSTEM_PROMPT = `You are an AI assistant specialized in creating conversation templates for professionals. Your role is to generate structured, practical templates that help users conduct effective conversations in their field.

Key Guidelines:
1. Create templates that flow naturally from discovery to action
2. Include a mix of open-ended questions and specific talking points
3. Consider the user's industry and experience level
4. Make elements actionable and specific
5. Include proper categorization for each element

Element Types:
- question: Open-ended or specific questions to ask
- statement: Key points or facts to communicate
- instruction: Actions or processes to explain
- note: Reminders or important considerations

Element Categories:
- discovery: Understanding needs, situation, goals
- presentation: Explaining solutions, features, benefits
- objection_handling: Addressing concerns or hesitations
- closing: Moving toward decisions or next steps
- follow_up: Post-conversation actions or scheduling

You must respond with valid JSON only, no additional text.`;

// Generate the main prompt for template creation
export const generateTemplatePrompt = (context: PromptContext): string => {
  const {
    userInput,
    industry = 'general',
    templateType = 'consultation',
    difficulty = 'beginner',
    estimatedDuration = '15-30 minutes',
    userHistory = [],
    existingTemplates = []
  } = context;

  let prompt = `${SYSTEM_PROMPT}

User Request: "${userInput}"

Context:
- Industry: ${industry}
- Template Type: ${templateType}
- Difficulty Level: ${difficulty}
- Estimated Duration: ${estimatedDuration}`;

  if (userHistory.length > 0) {
    prompt += `\n- User's Recent Templates: ${userHistory.slice(0, 3).join(', ')}`;
  }

  if (existingTemplates.length > 0) {
    prompt += `\n- Avoid Duplicating: ${existingTemplates.slice(0, 5).join(', ')}`;
  }

  prompt += `

Requirements:
1. Generate 5-12 conversation elements
2. Create a logical flow from introduction to conclusion
3. Include various element types and categories
4. Make it practical and immediately usable
5. Ensure elements are specific to the context

Generate a template as JSON matching this exact structure:
{
  "name": "Template Name",
  "description": "Brief description of when to use this template",
  "category": "general|discovery|consultation|presentation|follow_up",
  "elements": [
    {
      "type": "question|statement|instruction|note",
      "content": "The actual content/question/statement",
      "category": "discovery|presentation|objection_handling|closing|follow_up",
      "required": true|false,
      "placeholder": "Optional placeholder text"
    }
  ],
  "metadata": {
    "estimatedDuration": "X minutes",
    "difficulty": "beginner|intermediate|advanced",
    "industry": "industry name",
    "tags": ["tag1", "tag2", "tag3"]
  }
}`;

  return prompt;
};

// Industry-specific prompt enhancements
export const INDUSTRY_CONTEXTS = {
  'financial_services': {
    keywords: ['compliance', 'regulations', 'risk assessment', 'financial goals', 'investment timeline'],
    commonElements: [
      'What are your current financial goals?',
      'How would you describe your risk tolerance?',
      'What is your investment timeline?'
    ]
  },
  'real_estate': {
    keywords: ['property search', 'financing', 'market conditions', 'location preferences', 'timeline'],
    commonElements: [
      'What type of property are you looking for?',
      'What is your preferred location or area?',
      'What is your budget range?'
    ]
  },
  'healthcare': {
    keywords: ['symptoms', 'medical history', 'current medications', 'lifestyle factors', 'concerns'],
    commonElements: [
      'Can you describe your current symptoms?',
      'What is your relevant medical history?',
      'Are you currently taking any medications?'
    ]
  },
  'sales': {
    keywords: ['pain points', 'budget', 'decision process', 'timeline', 'stakeholders'],
    commonElements: [
      'What challenges are you currently facing?',
      'What is your budget for this solution?',
      'Who else is involved in the decision-making process?'
    ]
  },
  'consulting': {
    keywords: ['current state', 'desired outcomes', 'constraints', 'success metrics', 'implementation'],
    commonElements: [
      'What is the current state of your situation?',
      'What specific outcomes are you hoping to achieve?',
      'What constraints or limitations should we consider?'
    ]
  }
} as const;

// Template type specific structures
export const TEMPLATE_STRUCTURES = {
  'discovery': {
    flow: ['introduction', 'situation_analysis', 'needs_assessment', 'goal_setting', 'next_steps'],
    primaryCategory: 'discovery'
  },
  'presentation': {
    flow: ['opening', 'problem_identification', 'solution_presentation', 'benefits_explanation', 'call_to_action'],
    primaryCategory: 'presentation'
  },
  'consultation': {
    flow: ['relationship_building', 'discovery', 'analysis', 'recommendations', 'implementation_planning'],
    primaryCategory: 'consultation'
  },
  'follow_up': {
    flow: ['recap', 'progress_check', 'new_developments', 'next_actions', 'scheduling'],
    primaryCategory: 'follow_up'
  }
} as const;

// Enhanced prompt for specific use cases
export const generateEnhancedPrompt = (context: PromptContext): string => {
  const basePrompt = generateTemplatePrompt(context);
  
  // Add industry-specific context
  const industryContext = context.industry && INDUSTRY_CONTEXTS[context.industry as keyof typeof INDUSTRY_CONTEXTS];
  if (industryContext) {
    const industryEnhancement = `

Industry-Specific Guidance for ${context.industry}:
- Key concepts to consider: ${industryContext.keywords.join(', ')}
- Common effective elements: ${industryContext.commonElements.slice(0, 2).join(', ')}`;
    
    return basePrompt + industryEnhancement;
  }

  return basePrompt;
};

// Validation for generated templates
export const validateGeneratedTemplate = (template: any): template is GeneratedTemplate => {
  return (
    typeof template === 'object' &&
    typeof template.name === 'string' &&
    typeof template.description === 'string' &&
    typeof template.category === 'string' &&
    Array.isArray(template.elements) &&
    template.elements.length > 0 &&
    template.elements.every((element: any) => 
      typeof element.type === 'string' &&
      typeof element.content === 'string' &&
      typeof element.category === 'string' &&
      typeof element.required === 'boolean'
    ) &&
    typeof template.metadata === 'object' &&
    typeof template.metadata.estimatedDuration === 'string' &&
    typeof template.metadata.difficulty === 'string' &&
    typeof template.metadata.industry === 'string' &&
    Array.isArray(template.metadata.tags)
  );
}; 